import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cpFetch } from '@/lib/cpClient';

export type PlatformHealthFailure = {
  tool_name: string;
  app_id: string;
  failure_count: number;
  classification: string;
  last_failure: string;
  error_samples?: string[];
  diagnosis?: Record<string, unknown> | null;
  fix_event_id?: number | null;
  status: string;
};

export type PlatformHealthStatus = {
  ok: boolean;
  health_score: number;
  active_failures: PlatformHealthFailure[];
  active_issue_count?: number;
  failing_tools?: number;
  inactive_tools?: number;
  active_tools?: number;
  fixes_in_progress: number;
  resolved_24h: number;
  healthy_tools: number;
  total_tools: number;
  failure_trend: 'up' | 'down' | 'stable' | 'unknown';
  watcher_running: boolean;
  last_poll: string | null;
  watcher_error?: string | null;
  config?: {
    failure_count: number;
    window_minutes: number;
    auto_fix_enabled: boolean;
  };
  error?: string;
};

export type FixHistoryEvent = {
  id: number;
  event_time: string;
  tool_name: string;
  app_id: string;
  failure_count: number | null;
  classification: string | null;
  diagnosis?: Record<string, unknown> | null;
  fix_result?: Record<string, unknown> | null;
  status: string;
  auto_fixed: boolean;
};

export type FixHistoryResponse = {
  ok: boolean;
  events: FixHistoryEvent[];
  hours?: number;
  limit?: number;
  error?: string;
};

export function usePlatformHealth(enabled = true) {
  return useQuery<PlatformHealthStatus>({
    queryKey: ['platform-health'],
    queryFn: () => cpFetch('/auto-heal/status'),
    enabled,
    refetchInterval: 30_000,
    retry: 1,
  });
}

export function useFixHistory(options?: { hours?: number; limit?: number; enabled?: boolean }) {
  const hours = options?.hours ?? 24;
  const limit = options?.limit ?? 20;
  const enabled = options?.enabled ?? true;

  return useQuery<FixHistoryResponse>({
    queryKey: ['fix-history', hours, limit],
    queryFn: () => cpFetch(`/auto-heal/history?hours=${hours}&limit=${limit}`),
    enabled,
    refetchInterval: 30_000,
    retry: 1,
  });
}

export function useTriggerAutoHealFix() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { tool_name: string; app_id: string; auto_fix?: boolean; error_message?: string }) =>
      cpFetch('/auto-heal/trigger', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-health'] });
      queryClient.invalidateQueries({ queryKey: ['fix-history'] });
    },
  });
}

export function useDismissAutoHealIssue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) =>
      cpFetch(`/auto-heal/dismiss/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-health'] });
      queryClient.invalidateQueries({ queryKey: ['fix-history'] });
    },
  });
}
